import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack, FormControlLabel, Switch, Checkbox, CircularProgress, Typography, Box, IconButton, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import { createNewsletter, uploadNewsletterCoverImage, uploadNewsletterPdf } from '../../controllers/newsletterController';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';

const CreateNewsletterDialog = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isPublished, setIsPublished] = useState(false);
  const [sendPushNotification, setSendPushNotification] = useState(false);
  const [publishedAtUtc, setPublishedAtUtc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [showPdfUploadModal, setShowPdfUploadModal] = useState(false);

  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const reset = () => {
    setTitle('');
    setIsActive(true);
    setIsPublished(false);
    setSendPushNotification(false);
    setPublishedAtUtc('');
    setError('');
    setCoverImage(null);
    setPdfFile(null);
    setCoverImagePreview(null);
    setUploadingPdf(false);
    setShowPdfUploadModal(false);
    setFileInputKey(prev => prev + 1);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    setSubmitting(true);
    try {
      const publishedAtUtcValue = publishedAtUtc ? new Date(`${publishedAtUtc}:00`).toISOString() : null;
      const created = await createNewsletter({
        title,
        isActive,
        isPublished,
        publishedAtUtc: publishedAtUtcValue,
        sendPushNotification: Boolean(sendPushNotification),
      });
      
      // Eğer kapak görseli seçilmişse, onu da yükle
      if (coverImage instanceof File) {
        try {
          await uploadNewsletterCoverImage({ newsletterId: created.id, file: coverImage });
        } catch (e) {
          // Kapak görseli yüklemesi başarısız olabilir; bülten başarıyla oluşturulmuş olur
          console.warn('Kapak görseli yüklenemedi:', e);
        }
      }
      
      // Eğer PDF dosyası seçilmişse, onu da yükle
      if (pdfFile instanceof File) {
        try {
          setUploadingPdf(true);
          setShowPdfUploadModal(true);
          await uploadNewsletterPdf({ newsletterId: created.id, file: pdfFile });
        } catch (e) {
          // PDF yüklemesi başarısız olabilir; bülten başarıyla oluşturulmuş olur
          console.warn('PDF dosyası yüklenemedi:', e);
        } finally {
          setUploadingPdf(false);
          setShowPdfUploadModal(false);
        }
      }
      
      onCreated?.({ ...created, sendPushNotification: Boolean(sendPushNotification) });
      handleClose();
    } catch (err) {
      setError(err?.message || 'Bülten oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* PDF Yükleme Modalı */}
      <Dialog 
        open={showPdfUploadModal} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
          zIndex: 1400,
        }}
      >
        <DialogContent sx={{ py: 4, px: 3 }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={40} sx={{ color: '#0E4D94' }} />
            <Typography variant="body1" sx={{ fontSize: '14px', color: '#444444', textAlign: 'center' }}>
              Yüklediğiniz belgenin sıkıştırılması biraz zaman alabilir. Lütfen sayfayı kapatmayınız.
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Ana Modal */}
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Yeni Bülten
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3, alignItems: 'flex-start' }}>
              {/* Sol Taraf - Kapak Görseli */}
              <Box>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '3/2',
                    bgcolor: '#fafafa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '2px dashed #e6e6e6',
                  }}
                >
                  <IconButton
                    component="label"
                    disabled={submitting}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      backgroundColor: '#E7EDF4',
                      borderRadius: '6px',
                      width: 32,
                      height: 32,
                      zIndex: 1,
                      '&:hover': {
                        backgroundColor: '#CFDBEA',
                      },
                      '& svg': {
                        fontSize: '16px',
                        color: '#0E4D94',
                      },
                    }}
                  >
                    <EditIcon />
                    <input
                      key={`cover-image-${fileInputKey}`}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const selectedFile = e.target.files?.[0];
                        if (selectedFile) {
                          setCoverImage(selectedFile);
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setCoverImagePreview(reader.result);
                          };
                          reader.readAsDataURL(selectedFile);
                        }
                        e.target.value = '';
                      }}
                    />
                  </IconButton>

                  {coverImagePreview ? (
                    <Box
                      component="img"
                      src={coverImagePreview}
                      alt="Kapak fotoğrafı önizleme"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', maxWidth: '100%', maxHeight: '100%' }}
                    />
                  ) : (
                    <Stack spacing={1} alignItems="center">
                      <ImageIcon sx={{ fontSize: 48, color: '#a3a3a3' }} />
                      <Typography variant="body2" sx={{ fontSize: '12px', color: '#0E4D94', fontWeight: 500 }}>
                        600 × 400
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Box>

              {/* Sağ Taraf - İçerik Bilgileri ve Ayarlar */}
              <Box>
                <Stack spacing={3}>
                  {/* Başlık */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      Başlık
                    </Typography>
                    <TextField 
                      fullWidth
                      value={title} 
                      onChange={(e) => setTitle(e.target.value)} 
                      required 
                      placeholder=""
                      disabled={submitting}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '5px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                    />
                  </Box>

                  {/* PDF Dosyası */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      PDF Dosyası
                    </Typography>
                    {uploadingPdf && (
                      <Alert 
                        severity="info" 
                        sx={{ mb: 1.5, fontSize: '13px' }}
                        icon={<CircularProgress size={18} sx={{ color: '#0E4D94' }} />}
                      >
                        Yüklediğiniz belgenin boyutu sıkıştırılıyor. Lütfen sayfayı kapatmadan bekleyiniz
                      </Alert>
                    )}
                    {pdfFile ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                        {/* Mevcut PDF Dosyası */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.75,
                            backgroundColor: '#f5f5f5',
                            borderRadius: '5px',
                            border: '1px solid #e6e6e6',
                            maxWidth: '250px',
                          }}
                        >
                          <PictureAsPdfIcon sx={{ fontSize: '14px', color: '#F8285A' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '12px',
                              color: '#444444',
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                            }}
                          >
                            {pdfFile.name}
                          </Typography>
                        </Box>
                        {/* PDF Değiştir Butonu */}
                        <Box
                          component="label"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.75,
                            backgroundColor: '#ffffff',
                            borderRadius: '5px',
                            border: '1px dashed #e6e6e6',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: '#0E4D94',
                              backgroundColor: '#f0f0f0',
                            },
                          }}
                        >
                          <input
                            key={`pdf-file-${fileInputKey}`}
                            type="file"
                            accept=".pdf"
                            hidden
                            onChange={(e) => {
                              const selectedFile = e.target.files?.[0];
                              if (selectedFile) {
                                setPdfFile(selectedFile);
                              }
                              e.target.value = '';
                            }}
                            disabled={submitting}
                          />
                          <PictureAsPdfIcon sx={{ fontSize: '14px', color: '#0E4D94' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '12px',
                              color: '#444444',
                              fontWeight: 500,
                            }}
                          >
                            Değiştir
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        component="label"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.5,
                          py: 0.75,
                          backgroundColor: '#f5f5f5',
                          borderRadius: '5px',
                          border: '1px solid #e6e6e6',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          width: 'fit-content',
                          '&:hover': {
                            borderColor: '#0E4D94',
                            backgroundColor: '#f0f0f0',
                          },
                        }}
                      >
                        <input
                          key={`pdf-file-${fileInputKey}`}
                          type="file"
                          accept=".pdf"
                          hidden
                          onChange={(e) => {
                            const selectedFile = e.target.files?.[0];
                            if (selectedFile) {
                              setPdfFile(selectedFile);
                            }
                            e.target.value = '';
                          }}
                          disabled={submitting}
                        />
                        <PictureAsPdfIcon sx={{ fontSize: '14px', color: '#0E4D94' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#444444',
                            fontWeight: 500,
                          }}
                        >
                          PDF Dosyası Ekle
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Durum */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      Durum
                    </Typography>
                    <FormControlLabel 
                      control={
                        <Switch
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          disabled={submitting}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#0E4D94',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#0E4D94',
                            },
                          }}
                        />
                      }
                      label={isActive ? 'Aktif' : 'Pasif'}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '13px',
                          ml: 1,
                        },
                      }}
                    />
                  </Box>

                  {/* Yayınlandı */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      Yayınlandı
                    </Typography>
                    <FormControlLabel 
                      control={
                        <Switch
                          checked={isPublished}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setIsPublished(checked);
                            if (checked && !publishedAtUtc) {
                              const now = new Date();
                              const pad = (n) => String(n).padStart(2, '0');
                              setPublishedAtUtc(
                                `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
                              );
                            }
                          }}
                          disabled={submitting}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#0E4D94',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#0E4D94',
                            },
                          }}
                        />
                      }
                      label={isPublished ? 'Yayınlandı' : 'Yayınlanmadı'}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '13px',
                          ml: 1,
                        },
                      }}
                    />
                  </Box>

                  {/* Yayınlanma Tarihi */}
                  {isPublished && (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                        Yayınlanma Tarihi
                      </Typography>
                      <TextField
                        type="datetime-local"
                        fullWidth
                        value={publishedAtUtc}
                        onChange={(e) => setPublishedAtUtc(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={submitting}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '5px',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0E4D94',
                            },
                          },
                          '& .MuiInputBase-input': {
                            py: 1.2,
                            px: 1.5,
                            fontSize: '14px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e6e6e6',
                          },
                        }}
                      />
                    </Box>
                  )}

                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sendPushNotification}
                          onChange={(e) => setSendPushNotification(e.target.checked)}
                          disabled={submitting}
                          sx={{
                            color: '#a3a3a3',
                            '&.Mui-checked': {
                              color: '#0E4D94',
                            },
                          }}
                        />
                      }
                      label="Uygulama bildirimi gönderilsin mi?"
                      sx={{
                        ml: 0,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '13px',
                        },
                      }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '12px', color: '#a3a3a3', ml: 4, mt: -0.5 }}>
                      İşaretlenirse bülten başlığı tüm üyelere uygulama bildirimi olarak gönderilir.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !title}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Oluştur'}
        </Button>
      </DialogActions>
      </form>
    </Dialog>
    </>
  );
};

export default CreateNewsletterDialog;
